import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Sparkles, ArrowLeft, Mail, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API_URL}/security/forgot-password`, { email });
      setSubmitted(true);
      toast.success('Recovery instructions sent!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-accent-50 to-secondary-50 flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="bg-white/70 backdrop-blur-md rounded-3xl p-8 shadow-2xl border border-white/20">
          <div className="flex items-center justify-center gap-2 mb-8">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-primary to-accent flex items-center justify-center">
              <Sparkles className="w-7 h-7 text-white" />
            </div>
            <h1 className="text-3xl font-heading font-black text-gray-900">Socyy</h1>
          </div>

          {!submitted ? (
            <>
              <h2 className="text-2xl font-heading font-bold text-center mb-2 text-gray-900" data-testid="forgot-password-title">
                Forgot Password?
              </h2>
              <p className="text-center text-gray-600 mb-6">
                Enter your email and we'll send you a recovery link
              </p>

              <form onSubmit={handleSubmit} className="space-y-4 mb-6" data-testid="forgot-password-form">
                <div>
                  <Label htmlFor="email" className="font-semibold text-gray-700">Email Address</Label>
                  <div className="relative mt-1">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="rounded-xl border-2 h-12 pl-10"
                      placeholder="your@email.com"
                      required
                      data-testid="forgot-email-input"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full rounded-full bg-primary hover:bg-primary-700 text-white font-bold h-12 shadow-lg hover:scale-105 transition-transform"
                  data-testid="forgot-submit-btn"
                >
                  {loading ? 'Sending...' : 'Send Recovery Link'}
                </Button>
              </form>
            </>
          ) : (
            <div className="text-center py-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-heading font-bold mb-2 text-gray-900">Check Your Email</h2>
              <p className="text-gray-600 mb-6">
                If an account exists for <strong>{email}</strong>, you'll receive a password reset link shortly.
              </p>
              <Button
                onClick={() => setSubmitted(false)}
                variant="outline"
                className="rounded-full border-2"
                data-testid="try-another-email-btn"
              >
                Try another email
              </Button>
            </div>
          )}

          <Link 
            to="/login" 
            className="flex items-center justify-center gap-2 text-sm text-primary hover:text-primary-700 font-semibold"
            data-testid="back-to-login-link"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
